import React, { useContext, useState } from 'react';
import { Box, Typography, Divider, Collapse, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { SidebarContext } from '../../contexts/SidebarContext';
import { colors, typography } from 'theme/tokens';

export default function SidebarNavGroup({ label, collapsible, icon: Icon, children, defaultOpen = false }) {
  const { collapsed } = useContext(SidebarContext);
  // Initial value only — computed by the caller from the route so a hard reload
  // lands with the group already open when the active page is inside it. A
  // manual collapse afterward is not overridden; see Sidebar.jsx's
  // computeInitialState for why this matters on refresh specifically.
  const [open, setOpen] = useState(defaultOpen);

  // No group label — just render children directly
  if (!label) {
    return <Box sx={{ mb: 0.5 }}>{children}</Box>;
  }

  // Collapsible group (e.g. Settings)
  if (collapsible) {
    return (
      <Box sx={{ mb: 0.5 }}>
        {collapsed ? (
          <Divider sx={{ borderColor: colors.sidebar.divider, mx: 2, my: 1 }} />
        ) : (
          <ListItemButton
            onClick={() => setOpen((v) => !v)}
            sx={{
              mx: 1,
              mt: 1.5,
              mb: 0.5,
              borderRadius: '10px',
              color: colors.sidebar.text,
              minHeight: 46,
              px: 2,
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: colors.sidebar.hoverBg,
                color: colors.sidebar.textHover,
              },
            }}
          >
            {Icon && (
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit', justifyContent: 'center' }}>
                <Icon sx={{ fontSize: typography.fontSize[22] }} />
              </ListItemIcon>
            )}
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontSize: typography.fontSize[14],
                fontWeight: typography.fontWeight.semiBold,
                fontFamily: typography.fontFamily.inter,
                letterSpacing: '-0.01em',
              }}
            />
            {open ? (
              <ExpandLess sx={{ fontSize: typography.fontSize[18], opacity: 0.6 }} />
            ) : (
              <ExpandMore sx={{ fontSize: typography.fontSize[18], opacity: 0.6 }} />
            )}
          </ListItemButton>
        )}
        {collapsed ? (
          children
        ) : (
          <Collapse in={open} timeout="auto" unmountOnExit>
            {children}
          </Collapse>
        )}
      </Box>
    );
  }

  // Regular group header (non-collapsible)
  return (
    <Box sx={{ mb: 0.5 }}>
      {collapsed ? (
        <Divider sx={{ borderColor: colors.sidebar.divider, mx: 2, my: 1 }} />
      ) : (
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            px: 3,
            pt: 3,
            pb: 1,
            color: colors.sidebar.groupLabel,
            fontSize: typography.fontSize[11],
            fontWeight: typography.fontWeight.bold,
            fontFamily: typography.fontFamily.inter,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
      )}
      {children}
    </Box>
  );
}
